<script setup lang="ts">
// Import any components you want to use
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import MainNavbar from '@/components/MainNavbar.vue';
import { getRecommendedRegistries } from '@/firebase/registry-service';
// todo fix logo height
//import ElixirLogo from '@/components/ElixirLogo.vue';
import SoftButton from '@/components/SoftButton.vue';
import MainFooter from '@/components/MainFooter.vue';

const router = useRouter();

const findRegistry = () => {
  router.push({ name: 'FindRegistry' });
};

const goToDonate = () => {
  router.push({ name: 'Donate' });
};

// todo - iGive
// const installIGive = () => {
//   // Logic for iGive installation or redirect to iGive page
//   router.push({ name: 'IGive' });
// };

const createRegistry = () => {
  router.push({ name: 'SignUp' });
};

// Recommended registries data
const recommendedRegistries = ref<any[]>([]);
const loading = ref(true);

onMounted(async () => {
  try {
    recommendedRegistries.value = await getRecommendedRegistries();
  } catch (error) {
    console.error('Error getting recommended registries:', error);
  } finally {
    loading.value = false;
  }
});

// Testimonials data
const testimonials = ref([
  {
    id: 1,
    quote: 'I never expected people to care so much. The blanket and books I received made my treatments so much easier. Thank you!',
    name: 'Sarah',
    title: 'Stage 3 Cancer Fighter',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80'
  },
  {
    id: 2,
    quote: 'Having a registry removed the stress of people asking what they could do to help. I could just direct them to the list of things I actually needed.',
    name: 'David',
    title: 'Lung Cancer Survivor',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80'
  }
]);
</script>

<template>
  <div class="nav-wrapper">
    <MainNavbar style="border-bottom: none !important; box-shadow: none !important;" class="home-navbar" />
  </div>

  <main>
    <!-- Hero Section with Split Screen Design -->
    <div class="page-header min-vh-200">
      <div class="container">
        <div class="row">
          <div class="col-lg-12 text-center mb-4">
            <h1 class="text-white pt-2 display-3 hero-title">Handling Cancer is Hard. Giving Comfort is Easy.</h1>
            <p class="lead text-white mt-3 hero-subtitle">
              Thousands of cancer patients need essential items to ease their journey. <br>You can help by purchasing from their registry or making a simple donation.
            </p>
          </div>
        </div>

        <!-- Split Screen: Left for Donors, Right for Patients -->
        <div class="row mt-4">
          <!-- Left Side (Donors) -->
          <div class="col-md-6 border-end border-white">
            <div class="donor-section p-4 text-center">
              <h2 class="text-white mb-3">Give Hope. Give Comfort. Give What Matters Most.</h2>
              <p class="text-white mb-4">
                Every cancer patient has unique needs. You can make their journey easier by fulfilling their registry or giving where it's needed most.
              </p>
              <div class="d-grid gap-3 button-container">
                <SoftButton color="white" rounded class="btn-lg" @click="goToDonate">Support Today's Registrant</SoftButton>
                <SoftButton color="white" rounded class="btn-lg" @click="findRegistry">Find a Registry</SoftButton>
              </div>
            </div>
          </div>

          <!-- Right Side (Patients) -->
          <div class="col-md-6">
            <div class="patient-section p-4 text-center">
              <h2 class="text-white mb-3">Going Through Cancer? Let Us Help You.</h2>
              <p class="text-white mb-4">
                We'll connect you with donors who care that will get you the things you need.
              </p>
              <SoftButton color="white" rounded class="btn-lg" @click="createRegistry">Create Your Registry Now</SoftButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="card soft-card shadow-lg mx-3 mx-md-4 mt-n6">
      <div class="container">
        <div class="section text-center py-5">
          <h2 class="title text-primary font-weight-bold mb-5">How It Works</h2>

          <!-- For Donors -->
          <h3 class="text-soft-primary mb-4">For Donors</h3>
          <div class="row mb-5">
            <div class="col-md-4 mb-4 mb-md-0">
              <div class="info">
                <div class="icon-circle bg-gradient-primary">
                  <i class="material-icons icon-size text-white">search</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Find a Patient's Registry</h4>
                <p class="description text-soft-secondary">
                  Choose a specific cancer patient or browse recommended registries.
                </p>
              </div>
            </div>
            <div class="col-md-4 mb-4 mb-md-0">
              <div class="info">
                <div class="icon-circle bg-gradient-success">
                  <i class="material-icons icon-size text-white">shopping_cart</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Fulfill a Need</h4>
                <p class="description text-soft-secondary">
                  Select essential items to bring them comfort.
                </p>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info">
                <div class="icon-circle bg-gradient-primary">
                  <i class="material-icons icon-size text-white">volunteer_activism</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Make an Immediate Impact</h4>
                <p class="description text-soft-secondary">
                  Your gift is delivered directly to them.
                </p>
              </div>
            </div>
          </div>

          <!-- For Cancer Patients -->
          <h3 class="text-soft-primary mb-4">For Cancer Patients</h3>
          <div class="row">
            <div class="col-md-4 mb-4 mb-md-0">
              <div class="info">
                <div class="icon-circle bg-gradient-primary">
                  <i class="material-icons icon-size text-white">edit_note</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Create Your Registry</h4>
                <p class="description text-soft-secondary">
                  List the items that will help during your treatment.
                </p>
              </div>
            </div>
            <div class="col-md-4 mb-4 mb-md-0">
              <div class="info">
                <div class="icon-circle bg-gradient-success">
                  <i class="material-icons icon-size text-white">share</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Share It</h4>
                <p class="description text-soft-secondary">
                  We help connect your registry with donors who care.
                </p>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info">
                <div class="icon-circle bg-gradient-primary">
                  <i class="material-icons icon-size text-white">healing</i>
                </div>
                <h4 class="info-title mt-3 mb-2 text-soft-primary font-weight-bold">Receive Support</h4>
                <p class="description text-soft-secondary">
                  Donors fulfill your needs so you can focus on healing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recommended Registries Section -->
    <div class="section bg-light py-5">
      <div class="container">
        <h2 class="title text-primary font-weight-bold text-center mb-5">Meet Patients Who Need Your Help Today</h2>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Loading recommended registries...</p>
        </div>
        <div v-else class="row">
          <div v-for="registry in recommendedRegistries" :key="registry.id" class="col-md-4 mb-4">
            <div class="card shadow-sm h-100">
              <div class="patient-card-img-container">
                <img :src="registry.profilePhotoURL || 'https://via.placeholder.com/400x200'" class="card-img-top patient-card-img" :alt="registry.title">
              </div>
              <div class="card-body text-center">
                <h5 class="card-title text-soft-primary font-weight-bold">{{ registry.title }}</h5>
                <p class="card-text">{{ registry.shortBio }}</p>
                <SoftButton color="white" rounded class="mt-2" @click="router.push({ name: 'ViewRegistry', params: { id: registry.id } })">View Registry</SoftButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ways to Give Section -->
    <div class="section py-5">
      <div class="container">
        <h2 class="title text-primary font-weight-bold text-center mb-5">Three Simple Ways to Make a Difference</h2>

        <div class="row">
          <div class="col-md-4 mb-4 mb-md-0">
            <div class="ways-to-give-card text-center p-4 shadow-sm h-100">
              <div class="mb-4">
                <i class="material-icons icon-size text-primary">person_search</i>
              </div>
              <h4 class="text-soft-primary font-weight-bold">Find a Patient's Registry</h4>
              <p>Shop for a specific cancer patient.</p>
            </div>
          </div>

          <div class="col-md-4 mb-4 mb-md-0">
            <div class="ways-to-give-card text-center p-4 shadow-sm h-100">
              <div class="mb-4">
                <i class="material-icons icon-size text-primary">recommend</i>
              </div>
              <h4 class="text-soft-primary font-weight-bold">Shop Our Recommended Registry</h4>
              <p>Give where it's needed most.</p>
            </div>
          </div>
        </div>
        <div class="text-center mt-4">
          <SoftButton color="white" rounded class="btn-lg">Start Giving</SoftButton>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="section bg-light py-5">
      <div class="container">
        <h2 class="title text-primary font-weight-bold text-center mb-5">Your Gifts Change Lives.</h2>

        <div class="row">
          <div v-for="testimonial in testimonials" :key="testimonial.id" class="col-md-6 mb-4">
            <div class="card shadow-sm h-100 testimonial-card">
              <div class="card-body text-center p-4">
                <div class="testimonial-img-container mb-3">
                  <img :src="testimonial.image" class="testimonial-img" :alt="testimonial.name">
                </div>
                <p class="card-text testimonial-quote">"{{ testimonial.quote }}"</p>
                <h5 class="text-soft-primary font-weight-bold mb-1">{{ testimonial.name }}</h5>
                <p class="text-muted">{{ testimonial.title }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

  <MainFooter />
</template>

<style scoped>
.nav-wrapper {
  position: relative;
  z-index: 1000;
}

.nav-wrapper::after {
  display: none !important;
  content: none !important;
  border: none !important;
}

/* Create a pseudo-element to cover the white line */
.nav-wrapper::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background-color: var(--elixir-blue);
  z-index: 5;
}

.page-header {
  background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),
                    url('/images/authentic-pics/meg_Kev.png');
  background-size: cover;
  background-position: center;
  margin-top: -70px;
  padding-top: 18vh;
  padding-bottom: 10vh;
  position: relative;
  border-top: none !important;
  min-height: 60vh;
}

/* Remove the white line */
.page-header::before {
  content: none !important;
  display: none !important;
  border: none !important;
}

.hero-title {
  font-weight: 700;
  max-width: 800px;
  font-size: 3.5rem;
  margin: 0 auto 1.5rem;
}

.hero-subtitle {
  max-width: 800px;
  margin: 1rem auto;
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 1.7;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
}

.donor-section, .patient-section {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.donor-section h2, .patient-section h2 {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.2rem;
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
}

.donor-section p, .patient-section p {
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  font-weight: 400;
  line-height: 1.6;
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
}

.title {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  color: var(--elixir-blue);
}

.icon-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  margin: 0 auto 20px;
  transition: box-shadow 0.3s;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.icon-circle:hover {
  box-shadow: 0 0 25px rgba(44, 93, 189, 0.4);
}

.icon-size {
  font-size: 2.5rem;
}

/* Remove the hover transform from .info since it's now on .icon-circle */
.info {
  padding: 20px 15px;
}

.info-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.8rem;
}

.description {
  font-size: 1.1rem;
  line-height: 1.6;
  font-weight: 400;
  color: #444;
}

.soft-card {
  border-radius: var(--border-radius-lg);
  padding: 3rem 2rem;
  background-color: white;
}

.font-weight-bold {
  font-weight: 600;
}

.section {
  padding: 5rem 0;
}

.section.bg-light {
  background-color: #f8f9fa;
}

.patient-card-img-container {
  height: 200px;
  overflow: hidden;
}

.patient-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s;
}

.patient-card-img:hover {
  transform: scale(1.05);
}

.testimonial-img-container {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto;
  border: 3px solid var(--elixir-blue);
}

.testimonial-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.testimonial-quote {
  font-size: 1.25rem;
  font-style: italic;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  color: #444;
  font-weight: 400;
}

.testimonial-card {
  border-radius: 12px;
  transition: transform 0.3s, box-shadow 0.3s;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.ways-to-give-card {
  border-radius: 12px;
  transition: transform 0.3s, box-shadow 0.3s;
  border: 1px solid #eee;
  padding: 2rem 1.5rem;
}

.ways-to-give-card h4 {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 1rem 0;
}

.ways-to-give-card p {
  font-size: 1.1rem;
  color: #444;
  line-height: 1.5;
}

.ways-to-give-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

/*noinspection CssUnusedSymbol*/
.footer {
  background: linear-gradient(to right, var(--elixir-blue), var(--elixir-blue-dark));
}

.footer a:hover {
  opacity: 0.8;
}

.button-container {
  max-width: 360px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .donor-section, .patient-section {
    min-height: auto;
    padding: 2rem 1rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .border-end {
    border-right: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    margin-bottom: 2rem;
    padding-bottom: 2rem;
  }
}
</style>
